import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';
import { useDbtProjects, useDbtReadiness } from 'api/warehouse';
import Section, { Row } from 'components/pipelines/Section';
import { useTokens } from 'theme';
import { plural, whenLabel } from 'utils/ru';

import { useTranslation } from 'react-i18next';

const latest = (values) => values.filter(Boolean).sort().pop() || null;

/**
 * What is built from this load: the models of the warehouse's dbt project
 * whose raw tables this pipeline lands, read from the readiness report.
 *
 * Shown are the top of that set — models no other model in it reads from —
 * since those are the marts a person knows; the staging layer under them is
 * implied. A build is not tied to one load (the planner waits for every input
 * of a model), so this is the models' latest state, not "this run's build".
 */
export default function WhatItFeeds({ pipeline }) {
  const { t } = useTranslation();
  const warehouseId = pipeline?.warehouse?.public_id;
  const { data: projects } = useDbtProjects();
  const project = warehouseId
    ? (projects || []).find((p) => p.warehouse?.public_id === warehouseId && p.is_active)
    : null;
  const { data: report } = useDbtReadiness(project?.public_id);

  if (!project || !report) return null;

  const ownLeaves = new Set(
    (report.leaves || [])
      .filter((leaf) => leaf.pipeline?.public_id === pipeline.public_id)
      .map((leaf) => leaf.name),
  );
  if (!ownLeaves.size) return null;

  const fed = (report.nodes || []).filter(
    (node) => node.resource_type === 'model' && (node.leaves || []).some((l) => ownLeaves.has(l)),
  );
  if (!fed.length) return null;

  const read = new Set(fed.flatMap((node) => node.parents || []));
  const top = fed.filter((node) => !read.has(node.name));
  const waiting = fed.filter((node) => node.verdict === 'waiting').length;
  const failing = fed.filter((node) => node.verdict === 'backing_off').length;
  const builtAt = latest(fed.map((node) => node.last_success_at));

  return (
    <Section
      title={t('data.whatThisBuilds')}
      aside={
        <Link
          component={RouterLink}
          to={`/console/pipelines/preparation/${project.public_id}?tab=readiness`}
          underline="hover"
          sx={{ fontWeight: 700 }}
        >{t('data.workspacePreparation.openReadiness')}</Link>
      }
    >
      <MartChips names={top.map((node) => node.name)} />
      <Box sx={{ mt: 1 }}>
        <Row label={t('data.martsAndModels')}>{fed.length}</Row>
        <Row label={t('data.lastBuild')}>{builtAt ? whenLabel(builtAt) : t('data.notBuiltYet')}</Row>
        {waiting > 0 && (
          <Row label={t('data.waitingForData2')}>
            {waiting} {plural(waiting, [t('data.model'), t('data.models2'), t('data.models3')])}
          </Row>
        )}
        {failing > 0 && (
          <Row label={t('data.buildFailed')}>
            {failing} {plural(failing, [t('data.model'), t('data.models2'), t('data.models3')])}
          </Row>
        )}
      </Box>
    </Section>
  );
}

const MAX_CHIPS = 8;

function MartChips({ names }) {
  const { t } = useTranslation();
  const themeTokens = useTokens();
  const shown = names.slice(0, MAX_CHIPS);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {shown.map((name) => (
        <Box
          key={name}
          sx={{ px: 1, py: 0.25, borderRadius: '8px', bgcolor: themeTokens.raised, fontSize: '0.78rem', fontWeight: 600 }}
        >
          {name}
        </Box>
      ))}
      {names.length > MAX_CHIPS && (
        <Typography sx={{ fontSize: '0.78rem', color: themeTokens.inkMuted, alignSelf: 'center' }}>{t('data.andMore')}{' '}{names.length - MAX_CHIPS}
        </Typography>
      )}
    </Box>
  );
}
